import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
} from 'react';
import {
    IFilterOptionState,
    sortDirections,
} from '../../types/lists/listTypes';
import { buildEndPoint, mapFiltersToOdata } from '../../utils/odataEndPoint';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import api from '../../services/api';
import useLanguage from '../../customHooks/useLanguage';
import {
    IComplementaryDataState,
    ITaxComplementaryDataOption,
    ITaxesFiltersState,
    ITaxState,
    TaxListViewType,
} from '../../types/taxes/taxesTypes';
import { ISupplierTax } from '../../types/entities/ISupplierTax';
import { useODataMultipleQueries } from '../../customHooks/useODataMultipleQueries';
import { ISupplierTaxType } from '../../types/entities/ISupplierTaxType';
import { ISupplierTaxCalculationType } from '../../types/entities/ISupplierTaxCalculationType';
import { ISupplierTaxCalculationRuleType } from '../../types/entities/ISupplierTaxCalculationRuleType';
import { ISupplementCalculationType } from '../../types/entities/ISupplementCalculationType';
import {
    getFiltersInitialState,
    supplementCalculationTypeIcons,
    supplierTaxCalculationTypesIcons,
    supplierTaxTypesIcons,
    UnknownOptionIcons,
} from '../../utils/taxes/taxesListUtils';
import { adaptTaxFromDDBB } from '../../services/taxes/taxesAdapters';
import useTimeZone from '../../customHooks/useTimeZone';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';

interface TaxesContextType {
    isLoading: boolean;
    page: number;
    totalResults: number;
    sortedBy: string;
    sortDir: sortDirections;
    filters: ITaxesFiltersState;
    items: ITaxState[];
    currentResults: number;
    handleSearch: (pageNumber?: number) => Promise<void>;
    handleSortByField: (field: string) => void;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    resetFilters: () => void;
    handleFilterChange: (
        filterKey: string,
        value: string | number | null | Date | IFilterOptionState
    ) => void;
    supplierHotels: IComplementaryDataState;
    supplierTaxTypes: IComplementaryDataState;
    supplierTaxCalculationTypes: IComplementaryDataState;
    supplierTaxCalculationRuleTypes: IComplementaryDataState;
    supplementCalculationTypes: IComplementaryDataState;
    currencies: IComplementaryDataState;
    visibleView: TaxListViewType;
    changeVisibleView: (view: TaxListViewType) => void;
}

const ContextTaxes = createContext<TaxesContextType | undefined>(undefined);

export const ProviderTaxes = ({ children }: { children: ReactNode }) => {
    const language = useLanguage();
    const timeZone = useTimeZone();
    const { setErrorMessage } = useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [sortDir, setSortDir] = useState<sortDirections>(sortDirections.DESC);
    const [sortedBy, setSortedBy] = useState('DateLastUpdated');
    const resultsPerPage = 40;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const [items, setItems] = useState<ITaxState[]>([]);

    const FILTERS_INITIAL_STATE = getFiltersInitialState(timeZone);
    const [filters, setFilters] = useState(FILTERS_INITIAL_STATE);

    const handleFilterChange = (
        filterKey: string,
        value: string | number | null | Date | IFilterOptionState
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: {
                ...prevFilters[filterKey as keyof ITaxesFiltersState],
                value,
            },
        }));
    };

    const resetFilters = () => {
        setFilters(FILTERS_INITIAL_STATE);
        getTaxes(1, FILTERS_INITIAL_STATE);
    };

    const [visibleView, setVisibleView] = useState(TaxListViewType.TABLE);
    const changeVisibleView = (view: TaxListViewType) => {
        setVisibleView(view);
    };

    const getTaxes = async (pageNumber = 1, filtersState = filters) => {
        setPage(pageNumber);
        if (pageNumber === 1) setItems([]);
        const filtersToOdata = mapFiltersToOdata(filtersState);
        const endPoint = buildEndPoint(
            {
                entityName: readOnlyEntities.SupplierTax,
                query: {
                    select: [
                        'Id',
                        'Enabled',
                        'Name',
                        'Amount',
                        'Priority',
                        'Description',
                        'IdSupplierHotel',
                        'SupplierHotel/Name',
                        'IdSupplierHotelRoomType',
                        'SupplierHotelRoomType/Name',
                        'IdSupplierHotelMealPlan',
                        'SupplierHotelMealPlan/Name',
                        'IdSupplierHotelRatePlan',
                        'SupplierHotelRatePlan/Name',
                        'IdSupplierTaxType',
                        'SupplierTaxType/Key',
                        'SupplierTaxType/Name',
                        'SupplierTaxType/Caption',
                        'IdSupplierTaxCalculationType',
                        'SupplierTaxCalculationType/Key',
                        'SupplierTaxCalculationType/Name',
                        'SupplierTaxCalculationType/Caption',
                        'SupplierTaxCalculationType/LocalizedCaption',
                        'IdSupplierTaxCalculationRuleType',
                        'SupplierTaxCalculationRuleType/Key',
                        'SupplierTaxCalculationRuleType/Name',
                        'SupplierTaxCalculationRuleType/Caption',
                        'SupplierTaxCalculationRuleType/LocalizedCaption',
                        'IdSupplementCalculationType',
                        'SupplementCalculationType/Key',
                        'SupplementCalculationType/Name',
                        'SupplementCalculationType/Caption',
                        'SupplementCalculationType/LocalizedCaption',
                        'IdCurrency',
                        'Currency/Name',
                        'EffectivePerson',
                        'FromAgeEnabled',
                        'FromAge',
                        'FromDate',
                        'ToDate',
                    ],
                    expand: [
                        readOnlyEntities.SupplierHotel,
                        readOnlyEntities.SupplierHotelRoomType,
                        readOnlyEntities.SupplierHotelMealPlan,
                        readOnlyEntities.SupplierHotelRatePlan,
                        readOnlyEntities.SupplierTaxType,
                        readOnlyEntities.SupplierTaxCalculationType,
                        readOnlyEntities.SupplierTaxCalculationRuleType,
                        readOnlyEntities.SupplementCalculationType,
                        readOnlyEntities.Currency,
                    ],
                    filter: [...filtersToOdata],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: resultsPerPage,
                },
            },
            pageNumber
        );
        setIsLoading(true);
        try {
            const { data } = await api.get(endPoint);
            const { value: _items } = data;
            const _totalResults = parseInt(data['odata.count']);

            const newItems: ITaxState[] = _items.map((item: ISupplierTax) =>
                adaptTaxFromDDBB(item)
            );

            if (pageNumber === 1) {
                setItems(newItems);
                setTotalResults(_totalResults);
            } else {
                setItems(prev => [...prev, ...newItems]);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getTaxes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, sortedBy, sortDir]);

    const handleSearch = async (pageNumber = 1) => {
        await getTaxes(pageNumber);
    };

    const handleSortByField = (field: string) => {
        if (field === sortedBy) {
            setSortDir(prevSortDir =>
                prevSortDir === sortDirections.ASC
                    ? sortDirections.DESC
                    : sortDirections.ASC
            );
        } else {
            setSortedBy(field);
            setSortDir(sortDirections.ASC);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isOnBottom = scrollTop > scrollHeight - clientHeight - 1;
        if (isOnBottom && !isLoading && page < totalPages) {
            const nextPage = page + 1;
            setPage(nextPage);
            getTaxes(nextPage);
        }
    };

    const independentQueries = [
        {
            key: 'supplierHotels',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotel,
                select: ['Id', 'Name', 'IdCurrency'],
                orderBy: 'Name asc',
            },
        },
        {
            key: 'supplierTaxTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierTaxType,
                select: ['Id', 'Name', 'Caption', 'Key'],
                orderBy: 'Caption asc',
                filter: 'Enabled',
            },
            transform: (
                supTaxTypes: ISupplierTaxType[]
            ): ITaxComplementaryDataOption[] => {
                return supTaxTypes.map(data => ({
                    Id: data.Id,
                    Name: data.Caption || data.Name || '',
                    Key: data.Key,
                    Icon:
                        supplierTaxTypesIcons[
                            data.Key as keyof typeof supplierTaxTypesIcons
                        ] || UnknownOptionIcons.SUPPLIER_TAX_TYPE,
                }));
            },
        },
        {
            key: 'supplierTaxCalculationTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierTaxCalculationType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                taxCalcTypes: ISupplierTaxCalculationType[]
            ): ITaxComplementaryDataOption[] => {
                return taxCalcTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon:
                        supplierTaxCalculationTypesIcons[
                            data.Key as keyof typeof supplierTaxCalculationTypesIcons
                        ] || UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_TYPE,
                }));
            },
        },
        {
            key: 'supplierTaxCalculationRuleTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierTaxCalculationRuleType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                ruleTypes: ISupplierTaxCalculationRuleType[]
            ): ITaxComplementaryDataOption[] => {
                return ruleTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon: 'MoreHoriz',
                }));
            },
        },
        {
            key: 'supplementCalculationTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplementCalculationType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                supplementCalcTypes: ISupplementCalculationType[]
            ): ITaxComplementaryDataOption[] => {
                return supplementCalcTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon:
                        supplementCalculationTypeIcons[
                            data.Key as keyof typeof supplementCalculationTypeIcons
                        ] || UnknownOptionIcons.SUPPLEMENT_CALCULATION_TYPE,
                }));
            },
        },
        {
            key: 'currencies',
            endpoint: {
                entityName: readOnlyEntities.Currency,
                select: ['Id', 'Name'],
                orderBy: 'Name asc',
                filter: 'Enabled',
            },
        },
    ];

    const {
        supplierHotels,
        supplierTaxTypes,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useODataMultipleQueries({
        queries: independentQueries,
        dependencies: [],
    });

    return (
        <ContextTaxes.Provider
            value={{
                filters,
                items,
                currentResults: items.length,
                isLoading,
                page,
                totalResults,
                sortedBy,
                sortDir,
                handleSearch,
                handleSortByField,
                handleScroll,
                resetFilters,
                handleFilterChange,
                supplierHotels,
                supplierTaxTypes,
                supplierTaxCalculationTypes,
                supplierTaxCalculationRuleTypes,
                supplementCalculationTypes,
                currencies,
                visibleView,
                changeVisibleView,
            }}
        >
            {children}
        </ContextTaxes.Provider>
    );
};

export const useTaxesList = () => {
    const context = useContext(ContextTaxes);
    if (context === undefined) {
        throw new Error('useTaxesList must be used within a ProviderTaxes');
    }
    return context;
};

export const useTaxesComplementaryData = () => {
    const {
        supplierHotels,
        supplierTaxTypes,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useContext(ContextTaxes) as TaxesContextType;

    return {
        supplierHotels,
        supplierTaxTypes,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    };
};
